import React, { createContext, useContext } from 'react'

import { useI18n } from '@shopify/react-i18n'

import categoryContext from '@/context/category'
import customPageContext from '@/context/custom-page'
import matchupPageContext from '@/context/matchup-page'
import performerContext from '@/context/performer'
import productionDetailsContext from '@/context/production-details'
import ProductionListContext from '@/context/productions-list'
import subCategoryContext from '@/context/sub-category'
import { useLocalizedCurrencyFeature } from '@/optimizely/features/internationalization/hooks/localized-currency'
import type { Performer, Production } from '@/types/app-types'

import { DEFAULT_CURRENCY } from '../internationalization/constants'
import InternationalizationContext from '../internationalization/context'

import { articleSelector } from './selectors'

const defaultState = { selectors: { article: '' } }
const context = createContext(defaultState)
const { Provider } = context

const CMSProvider: React.FC<{
    children: React.ReactElement[] | React.ReactElement
    article?: string
    shouldFormatPrice?: boolean
}> = ({ children, article, shouldFormatPrice }) => {
    const [isLocalizedCurrencyEnabled] = useLocalizedCurrencyFeature()

    // Get performer (performer page)
    const { selectors: performerSelectors } = useContext(performerContext)
    let performer: Performer | undefined = performerSelectors[0]
    // If performer is an empty object then treat it as undefined
    if (performer && Object.keys(performer).length === 0) {
        performer = undefined
    }

    // Get productions (performer page)
    const { state: productionListState } = useContext(ProductionListContext)
    const productions: Production[] | undefined = productionListState.data?.items

    // Get production details (production page)
    const {
        selectors: { productionDetails },
    } = useContext(productionDetailsContext)

    // Get category (category pages)
    const {
        selectors: { category },
    } = useContext(categoryContext)

    // Get sub-category (sub-category pages)
    const {
        selectors: { subCategory },
    } = useContext(subCategoryContext)

    // Get matchup (matchup pages)
    const {
        selectors: { matchupPage },
    } = useContext(matchupPageContext)

    // Get custom (custom pages)
    const {
        selectors: { customPage },
    } = useContext(customPageContext)

    const {
        selectors: { selectedCurrency },
    } = useContext(InternationalizationContext)

    const [i18n] = useI18n()

    const currency = isLocalizedCurrencyEnabled ? selectedCurrency : DEFAULT_CURRENCY

    return (
        <Provider
            value={{
                selectors: {
                    article: articleSelector({
                        article,
                        lookupData: {
                            performer,
                            productions,
                            productionDetails,
                            category,
                            subCategory,
                            matchupPage,
                            customPage,
                        },
                        shouldFormatPrice,
                        currency,
                        i18n,
                    }),
                },
            }}
        >
            {children}
        </Provider>
    )
}

export { context as default, CMSProvider }
